// Pestaña «Stock y movimientos»: KPIs, stock por tienda, apartados ahora, traspasos abiertos y la lista única.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
    InventoryLensPeriod,
    InventoryLiveReservation,
    InventoryMovementEntry,
    InventoryStorePieces,
    InventoryStoreReserved,
} from '@/api/services/retail-catalog/backoffice';
import { cn } from '@/lib/cn';
import { reportPeriodLabel } from '@/shared/patterns/PeriodBand';
import { useRetailUsers } from '@/domains/retail/negocios/api/useRetailOrg';
import { useInventorySku } from '../../api/useInventorySku';
import { useSkuSaleRows } from '../../api/useSkuSaleRows';
import { useAllTransfers, useOpenTransfers } from '../../api/useInventoryMovements';
import { INVENTORY_QUERY_KEY } from '../../api/useInventarioLens';
import {
    skuDetailErrorMessage,
    stockFigures,
    storeDistributionRows,
    storeInfo,
    userDirectory,
    type StoreDirectory,
} from '../../lib/inventoryDetail';
import { coverageDisplay, type InventoryRow } from '../../lib/inventoryList';
import { openTransfersOf, type MovementKind } from '../../lib/inventoryMovements';
import { useInventoryAbilities } from '../../lib/inventoryPermissions';
import {
    floorStockFromBalance,
    floorStockNow,
    inboundInTransit,
    inTransitQty,
    stockMovementRows,
} from '../../lib/inventoryStockMovements';
import { SkuOpenTransfersCard } from '../movements/OpenTransfers';
import { RegisterMovementModal } from '../movements/RegisterMovementModal';
import { LiveReservationsCard } from './LiveReservationsCard';
import { MovementsCard, type MovementsState } from './MovementsCard';
import { StoreStockCard, type StoreStockActions } from './StoreStockCard';
import {
    IntegrationLockNotice,
    useIntegrationLock,
} from '../../../integration/components/IntegrationLock';

export interface StockTabContext {
    period: InventoryLensPeriod;
    /** Tienda del filtro; ausente en consolidado. */
    storeId: string | undefined;
    stores: StoreDirectory;
    zones: ReadonlyMap<string, string>;
    retailerId: string | null;
    today: string;
}

interface LedgerState {
    movements: InventoryMovementEntry[];
    piecesByStore: InventoryStorePieces[];
    reservedByStore: InventoryStoreReserved[];
    /** `undefined` si la respuesta no trae el campo. */
    liveReservations: InventoryLiveReservation[] | undefined;
    isPending: boolean;
    error: unknown;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    onRetry: () => void;
}

/** Libro de la ficha del SKU. */
function useLedger(row: InventoryRow, ctx: StockTabContext, includeHolds: boolean): LedgerState {
    const queryClient = useQueryClient();
    const query = useInventorySku(row.sku, ctx.period, ctx.storeId, includeHolds);
    const pages = query.data?.pages ?? [];
    return {
        movements: pages.flatMap((p) => p.movements),
        piecesByStore: pages.at(0)?.piecesByStore ?? [],
        reservedByStore: pages.at(0)?.reservedByStore ?? [],
        liveReservations: pages.at(0)?.liveReservations,
        isPending: query.isPending,
        error: query.error,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        // Reinicia desde la primera página: un nextToken vencido no se arregla repitiéndolo.
        onRetry: () => {
            void queryClient.resetQueries({ queryKey: [...INVENTORY_QUERY_KEY, 'sku', row.sku] });
        },
    };
}

function Kpi({ label, value, className }: { label: string; value: string; className?: string }) {
    return (
        <div className="rounded-md border border-border bg-card px-2.5 py-2">
            <div className="truncate text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </div>
            <div className={cn('mt-0.5 text-[17px] font-bold tabular-nums', className)}>
                {value}
            </div>
        </div>
    );
}

function soldLabel(period: InventoryLensPeriod): string {
    return period === 'all' ? 'Vendidas (todo)' : `Vendidas ${period}`;
}

export function StockMovementsTab({ row, ctx }: { row: InventoryRow; ctx: StockTabContext }) {
    const { canRegisterEntry, canMoveInventory, canReadTransfers } = useInventoryAbilities();
    const [open, setOpen] = useState<{ kind: MovementKind; storeId?: string } | null>(null);
    const [showHolds, setShowHolds] = useState(false);
    const ledger = useLedger(row, ctx, showHolds);
    const sales = useSkuSaleRows(row, ctx, true);
    const operable = row.kind !== 'KIT';
    // Con la cadena integrada las entradas, mermas y traspasos los manda Zentral.
    const { locked } = useIntegrationLock();
    const readsTransfers = canReadTransfers && operable;
    const openTransfers = openTransfersOf(
        useOpenTransfers(readsTransfers).data?.items ?? [],
        row.sku,
    );
    const allTransfers = useAllTransfers(readsTransfers).data?.items;
    // Un solo pedido del padrón de la cadena resuelve el autor de todos los renglones.
    const retailUsers = useRetailUsers(ctx.retailerId ?? '');
    const users = retailUsers.isLoading ? null : userDirectory(retailUsers.data ?? []);
    const storeCode = ctx.storeId ? storeInfo(ctx.stores, ctx.storeId).code : undefined;
    const periodNote = `${reportPeriodLabel(ctx.period)}${storeCode ? ` · ${storeCode}` : ''}`;

    const rows = stockMovementRows({
        movements: ledger.movements,
        transfers: allTransfers ?? [],
        sales: sales.rows,
        // Con el saldo por tienda el número sale de una sola respuesta, sin mezclar edades.
        floorNow: floorStockFromBalance(ledger.piecesByStore, ctx.storeId, () =>
            floorStockNow(row.stock, ledger.reservedByStore, ctx.storeId),
        ),
    });

    const kinds: MovementKind[] = [
        ...(canRegisterEntry ? (['ENTRY'] as const) : []),
        ...(canMoveInventory ? (['ADJUSTMENT', 'TRANSFER'] as const) : []),
    ];
    const actions: StoreStockActions | null =
        operable && !locked && kinds.length > 0
            ? {
                  entry: canRegisterEntry,
                  adjustment: canMoveInventory,
                  transfer: canMoveInventory,
                  onOpen: (kind, storeId) => {
                      setOpen({ kind, storeId });
                  },
              }
            : null;

    const storeRows = storeDistributionRows(
        row,
        ctx.stores,
        ledger.piecesByStore,
        ledger.reservedByStore,
    );
    const figures = stockFigures(
        storeRows.filter((store) => !ctx.storeId || store.storeId === ctx.storeId),
    );
    const coverage = coverageDisplay(row.coverage, row.stock);
    const inTransit = readsTransfers ? inTransitQty(openTransfers, row.sku, ctx.storeId) : null;
    const listState: MovementsState = {
        // Espera las ventas para no pintar la venta sin su detalle y luego saltar.
        isPending: ledger.isPending || (sales.available && sales.query.isPending),
        error: ledger.error,
        errorTitle: 'No se pudo cargar la ficha del SKU',
        errorMessage: skuDetailErrorMessage,
        hasNextPage: ledger.hasNextPage,
        isFetchingNextPage: ledger.isFetchingNextPage,
        onLoadMore: () => {
            ledger.fetchNextPage();
            if (sales.query.hasNextPage) void sales.query.fetchNextPage();
        },
        onRetry: ledger.onRetry,
    };

    return (
        <>
            <div
                role="group"
                aria-label="Indicadores del SKU"
                className="mb-2 grid grid-cols-3 gap-2"
            >
                <Kpi label="En tienda" value={String(figures.onHand)} />
                <Kpi
                    label="Apartados"
                    value={String(figures.reserved)}
                    className={figures.reserved > 0 ? 'text-warning' : undefined}
                />
                <Kpi label="Para vender" value={String(figures.forSale)} className="text-success" />
                <Kpi
                    label="En tránsito"
                    value={inTransit === null ? '—' : String(inTransit)}
                    className={inTransit ? 'text-info' : undefined}
                />
                <Kpi
                    label={soldLabel(ctx.period)}
                    value={row.sales === null ? '—' : String(row.sales)}
                />
                <Kpi label="Cobertura" value={coverage.text} />
            </div>
            <StoreStockCard
                rows={storeRows}
                period={ctx.period}
                zones={ctx.zones}
                inbound={inboundInTransit(openTransfers, row.sku)}
                actions={actions}
            />
            <IntegrationLockNotice message="lockedMovements" active={operable} />
            <LiveReservationsCard
                reservations={ledger.liveReservations}
                stores={ctx.stores}
                storeId={ctx.storeId}
            />
            {readsTransfers && openTransfers.length > 0 ? (
                <SkuOpenTransfersCard sku={row.sku} stores={ctx.stores} />
            ) : null}
            {sales.query.error ? (
                <p className="mb-2 px-0.5 text-[10.5px] italic text-muted-foreground">
                    No se pudieron leer las ventas del SKU: las ventas van sin vendedor ni cliente.
                </p>
            ) : null}
            <MovementsCard
                rows={rows}
                stores={ctx.stores}
                users={users}
                periodNote={periodNote}
                showBalance
                emptyText="Sin movimientos del SKU en el periodo seleccionado."
                state={listState}
                holds={{
                    shown: showHolds,
                    onToggle: () => {
                        setShowHolds((shown) => !shown);
                    },
                }}
            />
            {open ? (
                <RegisterMovementModal
                    isOpen
                    onClose={() => {
                        setOpen(null);
                    }}
                    row={row}
                    ctx={ctx}
                    kinds={kinds}
                    initialKind={open.kind}
                    initialStoreId={open.storeId}
                />
            ) : null}
        </>
    );
}
